import { useState } from "react";
import { useDebounceCallback } from "usehooks-ts";
import SelectComponent from "@via/components/select/select.tsx";
import { SelectOption } from "@via/utils/interfaces.tsx";
import { McpTransportType } from "common";

type FilterItem = { id: string; value: unknown };

type McpServerFilterProps = {
  columnFilters: FilterItem[];
  setColumnFilters: (
    filters: (prevFilters: FilterItem[]) => FilterItem[]
  ) => void;
  setPage: (page: number) => void;
  setPageSize: (pageSize: number) => void;
};

const McpServerFilter = ({
  columnFilters,
  setColumnFilters,
  setPage,
  setPageSize,
}: McpServerFilterProps) => {
  const [nameFilter, setNameFilter] = useState(
    (columnFilters.find(filter => filter.id === "name")?.value as string) || ""
  );

  const [transportTypeFilter, setTransportTypeFilter] = useState<string | null>(
    (columnFilters.find(filter => filter.id === "transportType")?.value as
      | string
      | null) || null
  );

  const [statusFilter, setStatusFilter] = useState<string | null>(
    (columnFilters.find(filter => filter.id === "isEnabled")?.value as
      | string
      | null) || null
  );

  const debounced = useDebounceCallback((id, value) => {
    setColumnFilters(prevFilters => {
      const updatedFilters = prevFilters.map(filter =>
        filter.id === id ? { ...filter, value } : filter
      );
      if (!prevFilters.find(filter => filter.id === id) && value !== "") {
        return [...updatedFilters, { id, value }];
      }
      return updatedFilters.filter(filter => filter.value !== "");
    });
    setPage(0);
    setPageSize(10);
  }, 300);

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setNameFilter(value);
    debounced("name", value);
  };

  const handleTransportTypeChange = (option: SelectOption | null) => {
    const value = option ? (option.value as string) : null;
    setTransportTypeFilter(value);
    setColumnFilters(prevFilters => {
      const updatedFilters = prevFilters.map(filter =>
        filter.id === "transportType" ? { ...filter, value } : filter
      );
      if (
        !prevFilters.find(filter => filter.id === "transportType") &&
        value !== null
      ) {
        return [...updatedFilters, { id: "transportType", value }];
      }
      return updatedFilters.filter(
        filter => filter.id !== "transportType" || filter.value !== null
      );
    });
    setPage(0);
    setPageSize(10);
  };

  const handleStatusChange = (option: SelectOption | null) => {
    const value = option ? (option.value as string) : null;
    setStatusFilter(value);
    setColumnFilters(prevFilters => {
      const updatedFilters = prevFilters.map(filter =>
        filter.id === "isEnabled" ? { ...filter, value } : filter
      );
      if (
        !prevFilters.find(filter => filter.id === "isEnabled") &&
        value !== null
      ) {
        return [...updatedFilters, { id: "isEnabled", value }];
      }
      return updatedFilters.filter(
        filter => filter.id !== "isEnabled" || filter.value !== null
      );
    });
    setPage(0);
    setPageSize(10);
  };

  const transportTypeOptions: SelectOption[] = [
    { label: "HTTP Streamable", value: McpTransportType.HttpStreamable },
    { label: "SSE", value: McpTransportType.SSE },
    { label: "WebSocket", value: McpTransportType.WebSocket },
  ];

  const statusOptions: SelectOption[] = [
    { label: "Enabled", value: "1" },
    { label: "Disabled", value: "0" },
  ];

  return (
    <div className="w-full my-2 flex flex-col items-baseline justify-between gap-2 md:flex-row">
      <div className="w-full flex-1">
        <input
          type="text"
          placeholder="Filter By Name"
          className="w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
          value={nameFilter}
          onChange={handleNameChange}
        />
      </div>
      <div className="w-full flex-1">
        <SelectComponent
          name="filter-transport-type"
          placeholder="Filter By Transport Type"
          options={transportTypeOptions}
          onChange={e => handleTransportTypeChange(e as SelectOption)}
          value={
            transportTypeFilter !== null
              ? {
                  label:
                    transportTypeOptions.find(
                      opt => opt.value === transportTypeFilter
                    )?.label || "Unknown",
                  value: transportTypeFilter,
                }
              : null
          }
          enableClear={true}
        />
      </div>
      <div className="w-full flex-1">
        <SelectComponent
          name="filter-status"
          placeholder="Filter By Status"
          options={statusOptions}
          onChange={e => handleStatusChange(e as SelectOption)}
          value={
            statusFilter !== null
              ? {
                  label:
                    statusOptions.find(opt => opt.value === statusFilter)
                      ?.label || "Unknown",
                  value: statusFilter,
                }
              : null
          }
          enableClear={true}
        />
      </div>
    </div>
  );
};

export default McpServerFilter;
